import type { ReactNode } from "react";
import { StyleSheet, Text, View, Pressable } from "react-native";
import { useTranslation } from "react-i18next";

import { webTapFloor, type UiTheme } from "../theme";

// <InfoPill> — THE small `?` affordance that reveals an explanation in place,
// plus <InfoPillBody>, the block it reveals. Extracted from the Progress
// panels' `PanelTitleRow` (which now composes these) when the program
// composer needed the same gesture on a table column header: the alternative
// was a second hand-rolled `?` circle, i.e. the copy §CONSOLIDAMENTO forbids.
//
// The pill is STATELESS on purpose — `open` and `onToggle` are props. A column
// header and a panel title place the pill and the body in different parts of
// their layout (the composer's body lands under the whole table, not under the
// header row), and that is only expressible when the caller owns the state.
//
// Why an affordance instead of standing text: a permanent hint line is furniture
// you read once and then scroll past forever. LG-23 (inbox 3d88179b) is exactly
// that failure — the AMRAP hint sat under EVERY exercise editor, permanently.
// The `?` keeps the explanation discoverable (the affordance is visible) without
// spending a line of the layout on it. [[feedback_quick_tip_pill_layout]]

export function InfoPill({
  theme,
  open,
  onToggle,
  a11yLabel,
}: {
  theme: UiTheme;
  open: boolean;
  onToggle: () => void;
  /** Defaults to the shared "info" label; pass one when several pills share a screen. */
  a11yLabel?: string;
}) {
  const { t } = useTranslation();
  const styles = pillStyles(theme);
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={a11yLabel ?? t("common.infoA11y")}
      // The pill is a disclosure, not a plain button: announce the state so a
      // screen-reader user knows the explanation is already showing.
      accessibilityState={{ expanded: open }}
      // react-native-web drops accessibilityState without a word; the aria
      // prop is what reaches the DOM there.
      aria-expanded={open}
      onPress={onToggle}
      hitSlop={8}
      style={({ pressed }) => [
        styles.pill,
        open && styles.pillOpen,
        pressed && styles.pillPressed,
      ]}
    >
      <Text style={[styles.glyph, open && styles.glyphOpen]}>?</Text>
    </Pressable>
  );
}

export function InfoPillBody({
  theme,
  text,
  title,
  children,
}: {
  theme: UiTheme;
  /** Absent: the body is `children` alone. */
  text?: string;
  /** Optional heading line, for a tip that reads as a question and answer. */
  title?: string;
  /** HI-8: a body richer than one line of text (PauseExplainer). */
  children?: ReactNode;
}) {
  const styles = pillStyles(theme);
  return (
    <View style={styles.body}>
      {title ? (
        <Text style={[styles.bodyText, styles.bodyTitle]} accessibilityRole="header">
          {title}
        </Text>
      ) : null}
      {text ? <Text style={styles.bodyText}>{text}</Text> : null}
      {children}
    </View>
  );
}

function pillStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    pill: {
      // 28 + hitSlop 8 → 44pt effective tap target on native (≥44pt a11y
      // rule); react-native-web's Pressable doesn't implement hitSlop at
      // all (DS-48), so the floor is enforced directly on web via
      // webTapFloor — undefined on native, a no-op there.
      width: 28,
      height: 28,
      minWidth: webTapFloor,
      minHeight: webTapFloor,
      borderRadius: 14,
      borderWidth: 1,
      borderColor: p.border,
      backgroundColor: p.bg,
      alignItems: "center",
      justifyContent: "center",
    },
    pillPressed: { opacity: 0.6 },
    // Open = toggle state, not a primary action: brand100 tint + brand outline
    // instead of a full brand fill (colour budget S2.3).
    pillOpen: {
      backgroundColor: p.brand100,
      borderColor: p.brand500,
    },
    glyph: {
      color: p.ink3,
      fontSize: ty.sizes.sm,
      fontWeight: "700",
      lineHeight: 16,
    },
    glyphOpen: { color: p.brand500 },
    body: {
      marginTop: s.s2,
      paddingHorizontal: s.s3,
      paddingVertical: s.s2,
      backgroundColor: p.bg,
      borderRadius: r.r2,
      borderLeftWidth: 3,
      borderLeftColor: p.borderStrong,
    },
    bodyText: {
      color: p.ink2,
      fontSize: ty.sizes.sm,
      lineHeight: ty.sizes.sm + 6,
    },
    bodyTitle: { color: p.ink, fontWeight: "700" },
  });
}
